/**
 * Phase G — language catalogue metadata for the LEARNER-facing UI only.
 * Admins have no language preference (owner ruling): the admin dashboard
 * is English, always, for everyone — nothing in this file is ever read by
 * anything under /admin. See i18n/config.ts and components/layout/
 * LearnerLayout.tsx for the two independent mechanisms that enforce that.
 *
 * Owner ruling: all 22 languages ship eventually; Arabic ships first,
 * alongside English. Only `en` (bundled eagerly) and `ar` (a small,
 * partial proof-of-concept lazy catalogue — see catalog/lang/ar/, NOT a
 * real translation pass) have any actual translated content as of Phase
 * G. The other 20 codes below are real, owner-approved future targets,
 * wired into the language picker now so its shape doesn't need to change
 * when a translation-generation round fills them in — selecting one
 * today simply falls back to English text everywhere via i18next's
 * `fallbackLng`, which is correct/expected, not a bug.
 */
export interface LanguageMeta {
  code: string;
  englishName: string;
  nativeName: string;
  rtl: boolean;
  /** Round 66, side note — true only once a real catalog exists for this
   *  code (English is bundled eagerly; every other language is checked
   *  against catalog/lang/<code>/ — see i18n/config.ts's lazyLanguageBackend).
   *  Everything else here is a real, owner-approved future target, kept in
   *  this one list so its shape doesn't change when translation content
   *  lands — just flip this flag. Selecting a `false` code still works
   *  today (i18next's fallbackLng serves English), it's just not offered
   *  in the picker dropdowns so a learner doesn't pick a language that
   *  visibly does nothing yet. */
  available: boolean;
}

export const LANGUAGES: LanguageMeta[] = [
  { code: 'en', englishName: 'English',              nativeName: 'English',          rtl: false, available: true  },
  { code: 'ar', englishName: 'Arabic',                nativeName: 'العربية',           rtl: true,  available: true  },
  { code: 'es', englishName: 'Spanish',               nativeName: 'Español',          rtl: false, available: false },
  { code: 'fr', englishName: 'French',                nativeName: 'Français',         rtl: false, available: false },
  { code: 'de', englishName: 'German',                nativeName: 'Deutsch',          rtl: false, available: false },
  { code: 'pt', englishName: 'Portuguese',             nativeName: 'Português',        rtl: false, available: false },
  { code: 'ru', englishName: 'Russian',                nativeName: 'Русский',          rtl: false, available: false },
  { code: 'zh', englishName: 'Chinese (Simplified)',   nativeName: '简体中文',           rtl: false, available: false },
  { code: 'ja', englishName: 'Japanese',               nativeName: '日本語',            rtl: false, available: false },
  { code: 'ko', englishName: 'Korean',                 nativeName: '한국어',            rtl: false, available: false },
  { code: 'hi', englishName: 'Hindi',                  nativeName: 'हिन्दी',            rtl: false, available: false },
  { code: 'tr', englishName: 'Turkish',                nativeName: 'Türkçe',           rtl: false, available: false },
  { code: 'vi', englishName: 'Vietnamese',             nativeName: 'Tiếng Việt',        rtl: false, available: false },
  { code: 'id', englishName: 'Indonesian',             nativeName: 'Bahasa Indonesia', rtl: false, available: false },
  { code: 'th', englishName: 'Thai',                   nativeName: 'ไทย',              rtl: false, available: false },
  { code: 'ur', englishName: 'Urdu',                   nativeName: 'اردو',             rtl: true,  available: false },
  { code: 'fa', englishName: 'Persian',                nativeName: 'فارسی',            rtl: true,  available: false },
  { code: 'pl', englishName: 'Polish',                 nativeName: 'Polski',           rtl: false, available: false },
  { code: 'nl', englishName: 'Dutch',                  nativeName: 'Nederlands',       rtl: false, available: false },
  { code: 'it', englishName: 'Italian',                nativeName: 'Italiano',         rtl: false, available: false },
  { code: 'uk', englishName: 'Ukrainian',              nativeName: 'Українська',        rtl: false, available: false },
  { code: 'sw', englishName: 'Swahili',                nativeName: 'Kiswahili',        rtl: false, available: false },
];

/** Round 66, side note — what the picker dropdowns actually offer. Filter,
 *  not a separate hand-maintained list, so it can never drift out of sync
 *  with LANGUAGES' own `available` flags. */
export const AVAILABLE_LANGUAGES: LanguageMeta[] = LANGUAGES.filter((l) => l.available);

export const DEFAULT_LANGUAGE = 'en';

export function getLanguageMeta(code: string): LanguageMeta {
  return LANGUAGES.find((l) => l.code === code) ?? LANGUAGES[0];
}

export function isRtlLanguage(code: string): boolean {
  return getLanguageMeta(code).rtl;
}
